// Step 5 of the client-side X12 pipeline (810 variant): walks one
// transaction's flat body segments (from step 2's envelope tree) into the
// loop structure a human reader thinks in — header, Name loops, IT1-style
// invoice-line loops, and the summary. Only ever runs against a transaction
// whose ST01 is "810"; anything else is left alone so it can still be
// shown, read-only, by the UI. Mirrors parser840.ts's pattern one-for-one,
// against the 810's own generic X12 005010 loop shape.

import type {EdiSegment} from './tokenizer';
import type {EdiEnvelope, EdiGroup, EdiTransaction} from './envelope';

export interface Header810 {
  big?: EdiSegment;
  cur?: EdiSegment;
  refs: EdiSegment[];
  per?: EdiSegment;
  pwk: EdiSegment[];
  dtm: EdiSegment[];
  itd?: EdiSegment;
  fob?: EdiSegment;
  cad?: EdiSegment;
  msg: EdiSegment[];
  n9: EdiSegment[];
  other: EdiSegment[];
}

export interface NameLoop810 {
  n1: EdiSegment;
  n2?: EdiSegment;
  n3?: EdiSegment;
  n4?: EdiSegment;
  n5?: EdiSegment;
  n6?: EdiSegment;
  n9: EdiSegment[];
  refs: EdiSegment[];
  per?: EdiSegment;
  other: EdiSegment[];
}

export interface ItemLoop810 {
  it1: EdiSegment;
  pid: EdiSegment[];
  refs: EdiSegment[];
  sac: EdiSegment[];
  txi: EdiSegment[];
  dtm: EdiSegment[];
  other: EdiSegment[];
}

export interface Summary810 {
  tds?: EdiSegment;
  sac: EdiSegment[];
  txi: EdiSegment[];
  ctt?: EdiSegment;
  other: EdiSegment[];
}

export interface Parsed810 {
  header: Header810;
  nameLoops: NameLoop810[];
  items: ItemLoop810[];
  summary: Summary810;
}

function attachHeaderSegment(header: Header810, s: EdiSegment): void {
  switch (s.id) {
    case 'BIG':
      header.big = s;
      break;
    case 'CUR':
      header.cur = s;
      break;
    case 'REF':
      header.refs.push(s);
      break;
    case 'PER':
      header.per = s;
      break;
    case 'PWK':
      header.pwk.push(s);
      break;
    case 'DTM':
      header.dtm.push(s);
      break;
    case 'ITD':
      header.itd = s;
      break;
    case 'FOB':
      header.fob = s;
      break;
    case 'CAD':
      header.cad = s;
      break;
    case 'MSG':
      header.msg.push(s);
      break;
    default:
      header.other.push(s);
      break;
  }
}

function attachNameLoopSegment(loop: NameLoop810, s: EdiSegment): void {
  switch (s.id) {
    case 'N2':
      loop.n2 = s;
      break;
    case 'N3':
      loop.n3 = s;
      break;
    case 'N4':
      loop.n4 = s;
      break;
    case 'N5':
      loop.n5 = s;
      break;
    case 'N6':
      loop.n6 = s;
      break;
    case 'N9':
      loop.n9.push(s);
      break;
    case 'REF':
      loop.refs.push(s);
      break;
    case 'PER':
      loop.per = s;
      break;
    default:
      loop.other.push(s);
      break;
  }
}

function attachItemSegment(item: ItemLoop810, s: EdiSegment): void {
  switch (s.id) {
    case 'PID':
      item.pid.push(s);
      break;
    case 'REF':
      item.refs.push(s);
      break;
    case 'DTM':
      item.dtm.push(s);
      break;
    default:
      item.other.push(s);
      break;
  }
}

function emptyHeader(): Header810 {
  return {refs: [], pwk: [], dtm: [], msg: [], n9: [], other: []};
}

function emptyItem(it1: EdiSegment): ItemLoop810 {
  return {it1, pid: [], refs: [], sac: [], txi: [], dtm: [], other: []};
}

const HEADER_ONLY_SEGMENT_IDS = new Set([
  'BIG',
  'CUR',
  'PWK',
  'ITD',
  'FOB',
  'CAD',
  'MSG',
]);

const NAME_LOOP_FOLLOWUP_SEGMENT_IDS = new Set([
  'N2',
  'N3',
  'N4',
  'N5',
  'N6',
  'N9',
  'REF',
  'PER',
]);

type Phase = 'header' | 'name' | 'item' | 'summary';

/**
 * Walks one 810 transaction's flat body segments (ST/SE excluded) into its
 * loop structure. Never throws — a segment that doesn't fit anywhere
 * sensible in the current loop is bucketed into that loop's `other` list
 * rather than the walk aborting; the validator is what reports on a
 * document that's missing or misordered.
 */
export function parse810Segments(segments: EdiSegment[]): Parsed810 {
  const header = emptyHeader();
  const nameLoops: NameLoop810[] = [];
  const items: ItemLoop810[] = [];
  const summary: Summary810 = {sac: [], txi: [], other: []};

  let phase: Phase = 'header';
  let currentNameLoop: NameLoop810 | undefined;
  let currentItem: ItemLoop810 | undefined;
  let pendingSac: EdiSegment[] = [];
  let pendingTxi: EdiSegment[] = [];

  const flushPendingInto = (item: ItemLoop810 | undefined): void => {
    if (!item) return;
    item.sac.push(...pendingSac);
    item.txi.push(...pendingTxi);
    pendingSac = [];
    pendingTxi = [];
  };

  for (const s of segments) {
    switch (s.id) {
      case 'N1':
        flushPendingInto(currentItem);
        phase = 'name';
        currentItem = undefined;
        currentNameLoop = {n1: s, n9: [], refs: [], other: []};
        nameLoops.push(currentNameLoop);
        break;

      case 'N2':
      case 'N3':
      case 'N4':
      case 'N5':
      case 'N6':
        if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        }
        break;

      case 'N9':
        if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        } else if (phase === 'header') {
          header.n9.push(s);
        }
        break;

      case 'IT1':
        flushPendingInto(currentItem);
        phase = 'item';
        currentNameLoop = undefined;
        currentItem = emptyItem(s);
        items.push(currentItem);
        break;

      case 'SAC':
        if (phase === 'item' && currentItem) {
          pendingSac.push(s);
        } else if (phase === 'summary') {
          summary.sac.push(s);
        } else if (phase === 'header') {
          header.other.push(s);
        }
        break;

      case 'TXI':
        if (phase === 'item' && currentItem) {
          pendingTxi.push(s);
        } else if (phase === 'summary') {
          summary.txi.push(s);
        } else if (phase === 'header') {
          header.other.push(s);
        }
        break;

      case 'TDS':
        flushPendingInto(currentItem);
        phase = 'summary';
        summary.tds = s;
        currentItem = undefined;
        currentNameLoop = undefined;
        break;

      case 'CTT':
        if (summary.tds === undefined) {
          summary.sac.push(...pendingSac);
          summary.txi.push(...pendingTxi);
          pendingSac = [];
          pendingTxi = [];
        } else {
          flushPendingInto(currentItem);
        }
        phase = 'summary';
        summary.ctt = s;
        currentItem = undefined;
        currentNameLoop = undefined;
        break;

      default:
        if (
          HEADER_ONLY_SEGMENT_IDS.has(s.id) &&
          (phase === 'name' || phase === 'item')
        ) {
          phase = 'header';
          currentNameLoop = undefined;
          currentItem = undefined;
          attachHeaderSegment(header, s);
        } else if (phase === 'header') {
          attachHeaderSegment(header, s);
        } else if (
          phase === 'name' &&
          currentNameLoop &&
          NAME_LOOP_FOLLOWUP_SEGMENT_IDS.has(s.id)
        ) {
          attachNameLoopSegment(currentNameLoop, s);
        } else if (phase === 'name') {
          phase = 'header';
          currentNameLoop = undefined;
          attachHeaderSegment(header, s);
        } else if (phase === 'item' && currentItem) {
          attachItemSegment(currentItem, s);
        } else if (phase === 'summary') {
          summary.other.push(s);
        }
        break;
    }
  }

  flushPendingInto(currentItem);

  return {header, nameLoops, items, summary};
}

export interface EdiTransactionWithParsed810 extends EdiTransaction {
  parsed810?: Parsed810;
}

export interface EdiGroupWithParsed810 extends Omit<EdiGroup, 'transactions'> {
  transactions: EdiTransactionWithParsed810[];
}

export interface EdiEnvelopeWithParsed810 extends Omit<EdiEnvelope, 'groups'> {
  groups: EdiGroupWithParsed810[];
}

export function attachParsed810(
  envelope: EdiEnvelope
): EdiEnvelopeWithParsed810 {
  return {
    ...envelope,
    groups: envelope.groups.map(group => ({
      ...group,
      transactions: group.transactions.map(transaction => ({
        ...transaction,
        parsed810:
          transaction.transactionSetCode === '810'
            ? parse810Segments(transaction.segments)
            : undefined,
      })),
    })),
  };
}
